import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StepIndicator } from '@/components/StepIndicator';
import { Button, SideBarModal } from '@/components';
import { Handover } from '@/types/common';
import {
  CreateHandoverRequest,
  UpdateHandoverRequest,
  HandoverChecklistItemInput,
  checkActiveHandover,
} from '@/services/api/handovers';
import { useMitraOptions, getMitraName } from '@/hooks/useMitraOptions';
import { vehiclesApi } from '@/services/api/vehicles';
import { getListProvider, Provider } from '@/services/api/providers';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail, isGcsUrl } from '@/utils';
import {
  HandoverFormStep1,
  HandoverFormStep2,
  HandoverDetailView,
} from './AddHandoverModal/';

interface AddHandoverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateHandoverRequest | UpdateHandoverRequest) => void;
  isLoading: boolean;
  initialData?: AddHandoverModalInitialData | null;
  isEditMode?: boolean;
  /** Forwarded to the detail view, which decides whether a date is still movable. */
  onEditSchedule?: () => void;
}

export type AddHandoverModalInitialData = Omit<
  Partial<Handover>,
  'driverID'
> & {
  driverID?: string | number;
  hubLocationID?: string;
  vehicleHubID?: string;
  hubLocationName?: string;
  businessUnit?: string;
  modelSellingPrice?: number;
  vehicleModelSellingPrice?: number;
  sellingPrice?: number;
  lockBookingFields?: boolean;
};

interface HandoverFormData {
  hubLocationID: string;
  vehicleHubID?: string;
  hubLocationName: string;
  businessUnit: string;
  vehicleID: string;
  driverID: number;
  driverName: string;
  driverCode: string;
  clientID: number;
  clientName: string;
  hubID: number;
  hubName: string;
  hubCityID: number;
  hubCityName: string;
  handoverDate: string;
  bastLink: string;
  notes: string;
  vehicleHandoverImageURL: string;
  odometer?: string;
  depositMitraToDash?: string;
  unitEquipment: string[];
  sellingPrice?: number;
  modelSellingPrice?: number;
  priceType?: 'default' | 'custom';
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

const AddHandoverModal: React.FC<AddHandoverModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData,
  isEditMode = false,
  onEditSchedule,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showDetailView, setShowDetailView] = useState(
    isEditMode && !!initialData
  );
  const [detailTab, setDetailTab] = useState<'details' | 'equipment'>(
    'details'
  );
  const [formData, setFormData] = useState<HandoverFormData>({
    hubLocationID: '',
    vehicleHubID: '',
    hubLocationName: '',
    businessUnit: '',
    vehicleID: '',
    driverID: 0,
    driverName: '',
    driverCode: '',
    clientID: 0,
    clientName: '',
    hubID: 0,
    hubName: '',
    hubCityID: 0,
    hubCityName: '',
    handoverDate: '',
    bastLink: '',
    notes: '',
    vehicleHandoverImageURL: '',
    odometer: '',
    depositMitraToDash: '',
    unitEquipment: [],
    sellingPrice: 0,
    modelSellingPrice: 0,
    priceType: 'default',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  // Legacy (non-GCS) BAST link from an existing record — kept only to show a
  // "re-upload" notice; it is NOT put into formData.bastLink so validation
  // forces the user to upload a real PDF before saving.
  const [legacyBastLink, setLegacyBastLink] = useState('');

  // API data for dropdowns
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [clients, setClients] = useState<Provider[]>([]);
  const [pitstops, setPitstops] = useState<Pitstop[]>([]);
  const [hubLocations, setHubLocations] = useState<Pitstop[]>([]);
  const [loadingHubLocations, setLoadingHubLocations] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  // Structured per-part checklist emitted by HandoverPartChecklist.
  const [checklistItems, setChecklistItems] = useState<
    HandoverChecklistItemInput[]
  >([]);
  const [checklistComplete, setChecklistComplete] = useState(false);

  const handleChecklistChange = useCallback(
    (items: HandoverChecklistItemInput[], complete: boolean) => {
      setChecklistItems(items);
      setChecklistComplete(complete);
    },
    []
  );

  // Active handover check states
  const [hasActiveHandover, setHasActiveHandover] = useState(false);
  const [isCheckingActiveHandover, setIsCheckingActiveHandover] =
    useState(false);

  // The mitra field is read-only while editing or when the booking locks it,
  // so the list is only fetched when it can actually be picked from.
  const isMitraLocked = isEditMode || !!initialData?.lockBookingFields;
  const mitra = useMitraOptions({ enabled: isOpen && !isMitraLocked });
  const { getDriver: getMitra } = mitra;

  useEffect(() => {
    if (isOpen) {
      loadClients();
      loadPitstops();
      loadHubLocations();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData && isOpen) {
      // Show detail view first for edit mode (only on initial load)
      setShowDetailView(isEditMode);

      // Type assertion to access all API fields
      const data = initialData as any;

      const hubLocId = data.hubLocationID
        ? data.hubLocationID.toString()
        : data.vehicleHubID
          ? data.vehicleHubID.toString()
          : '';
      const bu = data.vehicleBusinessUnit || data.businessUnit || '';

      setFormData({
        hubLocationID: hubLocId,
        vehicleHubID: data.vehicleHubID?.toString() || hubLocId,
        hubLocationName: data.hubLocationName || '',
        businessUnit: bu,
        vehicleID: data.vehicleID || '',
        driverID: Number(data.driverID) || 0,
        driverName: data.driverName || data.mitraName || '',
        driverCode: data.driverCode || '',
        clientID: data.clientID || 0,
        clientName: data.clientName || data.client || '',
        hubID: data.hubID || 0,
        hubName: data.hubName || data.pitstop || '',
        hubCityID: data.hubCityID || 0,
        hubCityName: data.hubCityName || '',
        handoverDate: data.handoverDate || '',
        // Only a GCS-hosted doc counts as an uploaded file the backend can
        // handle. A legacy external link is dropped here (surfaced separately
        // as a re-upload notice) so the required-field check forces re-upload.
        bastLink: isGcsUrl(data.bastLink) ? data.bastLink || '' : '',
        notes: data.notes || '',
        vehicleHandoverImageURL:
          data.vehicleHandoverImageURL || data.fotoKendaraan || '',
        odometer: data.odometer ? data.odometer.toString() : '',
        unitEquipment: data.lockBookingFields
          ? []
          : data.unitEquipment || data.returnUnitEquipment || [],
        sellingPrice: data.sellingPrice || 0,
        modelSellingPrice:
          data.modelSellingPrice || data.vehicleModelSellingPrice || 0,
        depositMitraToDash: data.depositMitraToDash
          ? data.depositMitraToDash.toString()
          : '',
        priceType: data.priceType,
      });

      // Surface a legacy external BAST link (non-GCS) so the field can prompt
      // for a re-upload instead of silently failing the backend move on save.
      setLegacyBastLink(
        data.bastLink && !isGcsUrl(data.bastLink) ? data.bastLink : ''
      );

      // Load vehicles filtered by hub + business unit in edit mode
      if (hubLocId && bu) {
        loadVehicles(hubLocId, bu);
      }
    } else if (isOpen) {
      setShowDetailView(false);
      setLegacyBastLink('');
      setFormData({
        hubLocationID: '',
        vehicleHubID: '',
        hubLocationName: '',
        businessUnit: '',
        vehicleID: '',
        driverID: 0,
        driverName: '',
        driverCode: '',
        clientID: 0,
        clientName: '',
        hubID: 0,
        hubName: '',
        hubCityID: 0,
        hubCityName: '',
        handoverDate: '',
        bastLink: '',
        notes: '',
        vehicleHandoverImageURL: '',
        odometer: '',
        unitEquipment: [],
        sellingPrice: 0,
        modelSellingPrice: 0,
        depositMitraToDash: '',
        priceType: 'default',
      });
    }
    setErrors({});
    setCurrentStep(1);
    setDetailTab('details');
  }, [initialData, isOpen, isEditMode]);

  const loadVehicles = async (hubID?: string, businessUnit?: string) => {
    if (!hubID || !businessUnit) return;
    try {
      setLoadingVehicles(true);
      const response = await vehiclesApi.getVehicles(1, 1000, undefined, {
        statuses: ['IDLE'],
        isAvailable: true,
        hubIDs: hubID,
        businessUnits: businessUnit,
      });
      setVehicles(response.data || []);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to load vehicles'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await getListProvider({ page: '1', size: '9999' });
      setClients(response.data || []);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to load clients'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const loadPitstops = async () => {
    try {
      const response = await getListPitstop({
        page: '1',
        size: '9999',
        type: 'PROVIDER_WAREHOUSE',
      });
      setPitstops(response.data || []);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to load pitstops'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const loadHubLocations = async () => {
    try {
      setLoadingHubLocations(true);
      const response = await getListPitstop({
        page: '1',
        size: '9999',
        type: 'HUB',
        hasStaff: true,
      });
      setHubLocations(response.data || []);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to load hub locations'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setLoadingHubLocations(false);
    }
  };

  const handleInputChange = (
    field: keyof HandoverFormData,
    value: string | string[] | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDriverChange = async (driverId: string) => {
    const selectedDriver = getMitra(driverId);

    if (selectedDriver) {
      handleInputChange('driverID', selectedDriver.ID || selectedDriver.id);
      handleInputChange('driverName', getMitraName(selectedDriver));
      handleInputChange('driverCode', selectedDriver.code || '');

      // Get original driver ID from initialData if in edit mode
      const originalDriverID =
        isEditMode && initialData ? (initialData as any).driverID : null;

      const isOriginalDriver =
        isEditMode &&
        originalDriverID === (selectedDriver.ID || selectedDriver.id);

      if (!isOriginalDriver) {
        try {
          setIsCheckingActiveHandover(true);
          const response = await checkActiveHandover(driverId);
          setHasActiveHandover(response.hasActiveHandover || false);
        } catch (error) {
          console.error('Error checking active handover:', error);
          setHasActiveHandover(false);
        } finally {
          setIsCheckingActiveHandover(false);
        }
      } else {
        // If it's the original driver in edit mode, no need to check
        setHasActiveHandover(false);
      }
    } else {
      handleInputChange('driverID', 0);
      handleInputChange('driverName', '');
      handleInputChange('driverCode', '');
      setHasActiveHandover(false);
    }
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = clients.find(
      (client) => (client.ID || client.id).toString() === clientId
    );
    if (selectedClient) {
      handleInputChange('clientID', selectedClient.ID || selectedClient.id);
      handleInputChange('clientName', selectedClient.name);
    } else {
      handleInputChange('clientID', 0);
      handleInputChange('clientName', '');
    }
  };

  const handleHubLocationChange = (hubId: string) => {
    const selectedHub = hubLocations.find(
      (hub) => (hub.ID || hub.id).toString() === hubId
    );
    if (selectedHub) {
      const hubIdStr = (selectedHub.ID || selectedHub.id).toString();
      handleInputChange('hubLocationID', hubIdStr);
      handleInputChange('vehicleHubID', hubIdStr);
      handleInputChange('hubLocationName', selectedHub.name);
      // Reset vehicle selection and reload vehicles with new hub
      handleInputChange('vehicleID', '');
      setVehicles([]);
      loadVehicles(hubIdStr, formData.businessUnit);
    } else {
      handleInputChange('hubLocationID', '');
      handleInputChange('vehicleHubID', '');
      handleInputChange('hubLocationName', '');
      handleInputChange('vehicleID', '');
      setVehicles([]);
    }
  };

  const handleBusinessUnitChange = (value: string) => {
    handleInputChange('businessUnit', value);
    // Reset vehicle selection and reload vehicles with new business unit
    handleInputChange('vehicleID', '');
    setVehicles([]);
    loadVehicles(formData.hubLocationID, value);
  };

  const handlePitstopChange = (pitstopId: string) => {
    const selectedPitstop = pitstops.find(
      (pitstop) => (pitstop.ID || pitstop.id).toString() === pitstopId
    );
    if (selectedPitstop) {
      handleInputChange('hubID', selectedPitstop.ID || selectedPitstop.id);
      handleInputChange('hubName', selectedPitstop.name);
      // Extract city data from nested city object
      handleInputChange(
        'hubCityID',
        selectedPitstop.city?.id || selectedPitstop.cityID || 0
      );
      handleInputChange(
        'hubCityName',
        selectedPitstop.city?.name || selectedPitstop.cityName || ''
      );
    } else {
      handleInputChange('hubID', 0);
      handleInputChange('hubName', '');
      handleInputChange('hubCityID', 0);
      handleInputChange('hubCityName', '');
    }
  };

  const handleVehicleChange = (vehicleId: string) => {
    const selectedVehicle = vehicles.find(
      (vehicle) => (vehicle.ID || vehicle.id).toString() === vehicleId
    );

    handleInputChange('vehicleID', vehicleId);

    // Only update selling price if default price type is selected or not set
    if (
      selectedVehicle &&
      (formData.priceType === 'default' || !formData.priceType)
    ) {
      handleInputChange('sellingPrice', selectedVehicle.modelSellingPrice);
    } else if (!selectedVehicle) {
      // Reset to default if no vehicle selected
      handleInputChange('sellingPrice', 0);
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!isEditMode && !formData.hubLocationID) {
      newErrors.hubLocationID = 'Hub Location is required';
    }
    if (!isEditMode && !formData.businessUnit) {
      newErrors.businessUnit = 'Business Unit is required';
    }
    if (!formData.vehicleID) {
      newErrors.vehicleID = 'Vehicle is required';
    }
    if (!formData.clientID || formData.clientID === 0) {
      newErrors.clientID = 'Client is required';
    }
    if (!formData.handoverDate) {
      newErrors.handoverDate = 'Handover Date is required';
    }
    if (!formData.bastLink) {
      newErrors.bastLink = 'BAST document is required';
    }
    if (
      formData.depositMitraToDash === '' ||
      formData.depositMitraToDash === undefined
    ) {
      newErrors.depositMitraToDash = 'Deposit Mitra to Dash is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    // Block if driver has active handover
    if (hasActiveHandover) {
      return;
    }

    if (validateStep1()) {
      setCurrentStep(2);
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }
  };

  const validateEquipment = (): boolean => {
    // Every item marked Ada or Tidak ada; whether a photo counts too is the
    // checklist's own call (web_fms_handover_checklist_require_photo).
    if (!checklistComplete) {
      showErrorToast(
        'Error',
        'Lengkapi checklist: tandai tiap kelengkapan dan foto yang diperlukan.'
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEquipment()) {
      return;
    }

    try {
      const userEmail = getUserEmail();

      if (isEditMode) {
        const updatePayload: UpdateHandoverRequest = {
          vehicleID: formData.vehicleID,
          driverID: null,
          driverName: null,
          driverCode: null,
          clientID: formData.clientID,
          clientName: formData.clientName,
          hubID: formData.hubID,
          hubName: formData.hubName,
          hubCityID: formData.hubCityID,
          hubCityName: formData.hubCityName,
          handoverDate: formData.handoverDate,
          bastLink: formData.bastLink,
          notes: formData.notes,
          odometer: formData.odometer
            ? parseInt(formData.odometer, 10)
            : undefined,
          depositMitraToDash:
            parseFloat(formData.depositMitraToDash || '0') || 0,
          checklistItems,
          sellingPrice: formData.sellingPrice,
          updatedBy: userEmail,
        };

        // Only include driver fields if they have valid values
        if (formData.driverID && formData.driverID !== 0) {
          updatePayload.driverID = formData.driverID;
        }
        if (
          formData.driverName &&
          formData.driverName.trim() !== '' &&
          formData.driverName.trim() !== '-'
        ) {
          updatePayload.driverName = formData.driverName;
        }
        if (
          formData.driverCode &&
          formData.driverCode.trim() !== '' &&
          formData.driverCode.trim() !== '-'
        ) {
          updatePayload.driverCode = formData.driverCode;
        }

        await onSubmit(updatePayload);
      } else {
        const createPayload: CreateHandoverRequest = {
          vehicleID: formData.vehicleID,
          driverID: null,
          driverName: null,
          driverCode: null,
          clientID: formData.clientID,
          clientName: formData.clientName,
          hubID: formData.hubID,
          hubName: formData.hubName,
          hubCityID: formData.hubCityID,
          hubCityName: formData.hubCityName,
          handoverDate: formData.handoverDate,
          bastLink: formData.bastLink,
          notes: formData.notes,
          odometer: formData.odometer
            ? parseInt(formData.odometer, 10)
            : undefined,
          depositMitraToDash:
            parseFloat(formData.depositMitraToDash || '0') || 0,
          checklistItems,
          sellingPrice: formData.sellingPrice,
          createdBy: userEmail,
        };

        // Only include driver fields if they have valid values
        if (formData.driverID && formData.driverID !== 0) {
          createPayload.driverID = formData.driverID;
        }
        if (
          formData.driverName &&
          formData.driverName.trim() !== '' &&
          formData.driverName.trim() !== '-'
        ) {
          createPayload.driverName = formData.driverName;
        }
        if (
          formData.driverCode &&
          formData.driverCode.trim() !== '' &&
          formData.driverCode.trim() !== '-'
        ) {
          createPayload.driverCode = formData.driverCode;
        }

        await onSubmit(createPayload);
      }
    } catch (error) {}
  };

  const handleClose = () => {
    setFormData({
      hubLocationID: '',
      vehicleHubID: '',
      hubLocationName: '',
      businessUnit: '',
      vehicleID: '',
      driverID: 0,
      driverName: '',
      driverCode: '',
      clientID: 0,
      clientName: '',
      hubID: 0,
      hubName: '',
      hubCityID: 0,
      hubCityName: '',
      handoverDate: '',
      bastLink: '',
      notes: '',
      vehicleHandoverImageURL: '',
      odometer: '',
      unitEquipment: [],
      sellingPrice: 0,
      modelSellingPrice: 0,
      depositMitraToDash: '',
      priceType: 'default',
    });
    setErrors({});
    setCurrentStep(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        // Handover create/edit/detail all read best as a full-page centered
        // modal — consistent with the return flow.
        position="center"
        width="full"
        contentRef={scrollRef}
        header={
          <div>
            <div className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showDetailView
                      ? 'Handover Details'
                      : isEditMode
                        ? 'Edit Handover'
                        : 'Add Handover'}
                  </h2>
                  <p className="text-sm font-normal text-gray-600">
                    {showDetailView
                      ? 'Review handover information'
                      : isEditMode
                        ? 'Edit handover details.'
                        : 'New handover details.'}
                  </p>
                </div>
              </div>

              {!showDetailView && (
                <div className="flex justify-center pb-2 mt-3">
                  <StepIndicator
                    steps={[
                      { id: 1, label: 'Handover Details' },
                      { id: 2, label: 'Equipment Checklist' },
                    ]}
                    currentStep={currentStep}
                  />
                </div>
              )}
            </div>
          </div>
        }
        footer={
          showDetailView ? (
            <div className="flex gap-3">
              <Button
                variant="neutral"
                onClick={handleClose}
                className="flex-1 py-3"
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowDetailView(false);
                  setCurrentStep(1);
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = 0;
                  }
                }}
                className="flex-1 py-3"
              >
                Edit
              </Button>
            </div>
          ) : currentStep === 1 ? (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={
                hasActiveHandover ||
                isCheckingActiveHandover ||
                !formData.vehicleID ||
                !formData.clientID ||
                !formData.hubID ||
                !formData.handoverDate ||
                !formData.bastLink ||
                formData.depositMitraToDash === '' ||
                formData.depositMitraToDash === undefined
              }
              className="w-full py-3"
            >
              Next
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="neutral"
                onClick={() => {
                  setCurrentStep(1);
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = 0;
                  }
                }}
                className="flex-1 py-3"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isLoading || !checklistComplete}
                className="flex-1 py-3"
              >
                {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
              </Button>
            </div>
          )
        }
      >
        <div className="flex-1">
          {showDetailView ? (
            <HandoverDetailView
              detailTab={detailTab}
              formData={formData}
              initialData={initialData}
              vehicleEquipment={formData.unitEquipment}
              onEditSchedule={onEditSchedule}
              onTabChange={(tab) => {
                setDetailTab(tab);
                setTimeout(() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = 0;
                  }
                }, 0);
              }}
            />
          ) : (
            <>
              {/* Step Content */}
              <div className="pb-40">
                {currentStep === 1 ? (
                  <HandoverFormStep1
                    formData={formData}
                    errors={errors}
                    vehicles={vehicles}
                    clients={clients}
                    pitstops={pitstops}
                    hubLocations={hubLocations}
                    loadingHubLocations={loadingHubLocations}
                    mitra={mitra}
                    isEditMode={isEditMode}
                    lockBookingFields={
                      !isEditMode && !!initialData?.lockBookingFields
                    }
                    initialData={initialData}
                    hasActiveHandover={hasActiveHandover}
                    isCheckingActiveHandover={isCheckingActiveHandover}
                    loadingVehicles={loadingVehicles}
                    legacyBastLink={legacyBastLink}
                    onInputChange={handleInputChange}
                    onVehicleChange={handleVehicleChange}
                    onClientChange={handleClientChange}
                    onPitstopChange={handlePitstopChange}
                    onDriverChange={handleDriverChange}
                    onHubLocationChange={handleHubLocationChange}
                    onBusinessUnitChange={handleBusinessUnitChange}
                  />
                ) : (
                  <HandoverFormStep2
                    odometer={formData.odometer}
                    notes={formData.notes}
                    onOdometerChange={(value) =>
                      handleInputChange('odometer', value)
                    }
                    onNotesChange={(value) => handleInputChange('notes', value)}
                    onChecklistChange={handleChecklistChange}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </SideBarModal>
    </>
  );
};

export default AddHandoverModal;
